import React, { useRef, useState, useEffect, useMemo } from 'react';

const UICustomerDropdown = ({
  options,
  selectedOption,
  onSelect,
  enableSearch,
  serverSearch = false,
  onSearchChange,
  searchLoading = false,
  searchMinLength = 2,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const dropdownRef = useRef(null);
  const listRef = useRef(null);
  const selectedItemRef = useRef(null);

  useEffect(() => {
    if (serverSearch) {
      setFilteredOptions(options);
      return;
    }
    const term = searchTerm.toLowerCase();
    setFilteredOptions(
      options.filter(
        (option) =>
          option?.label?.toLowerCase().includes(term) ||
          option?.phone?.toLowerCase().includes(term) ||
          option?.email?.toLowerCase().includes(term) ||
          option?.client_code?.toLowerCase().includes(term)
      )
    );
  }, [searchTerm, options, serverSearch]);

  useEffect(() => {
    if (!serverSearch || !onSearchChange) return undefined;

    const timer = setTimeout(() => {
      onSearchChange(searchTerm.trim());
    }, 350);

    return () => clearTimeout(timer);
  }, [searchTerm, serverSearch, onSearchChange]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (option) => {
    onSelect(option);
    setIsOpen(false);
    setSearchTerm('');
  };

  const showTypeToSearch =
    serverSearch && searchTerm.trim().length > 0 && searchTerm.trim().length < searchMinLength;

  const selectedLabel =
    selectedOption?.label ||
    options.find((o) => o.value === selectedOption?.value)?.label ||
    'Sélectionner une option';

  const displayOptions = useMemo(() => {
    if (!selectedOption?.value) return filteredOptions;
    if (filteredOptions.some((o) => o.value === selectedOption.value)) {
      return filteredOptions;
    }
    return [selectedOption, ...filteredOptions];
  }, [filteredOptions, selectedOption]);

  useEffect(() => {
    if (!isOpen || !selectedOption?.value || searchLoading) return;
    const frameId = requestAnimationFrame(() => {
      selectedItemRef.current?.scrollIntoView({ block: 'nearest' });
    });
    return () => cancelAnimationFrame(frameId);
  }, [isOpen, displayOptions, selectedOption?.value, searchLoading]);

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        className="relative flex items-center form-control justify-between cursor-pointer w-full px-3 py-1.5 text-gray-900 bg-white"
        onClick={() => setIsOpen(!isOpen)}
      >
        <input
          type="text"
          value={selectedLabel}
          readOnly
          className="w-full outline-none pointer-events-none"
        />
        <div className="border-l-2 pl-2">{isOpen ? '▲' : '▼'}</div>
      </div>
      {isOpen && (
        <div className="absolute top-full left-0 right-0 border border-gray-300 bg-white z-50 rounded-b-md shadow-sm">
          {enableSearch && (
            <div className="p-2 border-bottom">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={
                  serverSearch
                    ? 'Rechercher (nom, code, téléphone, e-mail)…'
                    : 'Rechercher…'
                }
                className="form-control"
                onClick={(e) => e.stopPropagation()}
              />
              {serverSearch && (
                <p className="small text-muted mb-0 mt-1">
                  {showTypeToSearch
                    ? `Saisissez au moins ${searchMinLength} caractères`
                    : searchLoading
                      ? 'Recherche en cours…'
                      : 'Recherche dans toute la base clients'}
                </p>
              )}
            </div>
          )}
          <div className="max-h-48 overflow-y-auto no-scrollbar" ref={listRef}>
            {searchLoading ? (
              <div className="p-2 text-gray-500 text-center">Chargement…</div>
            ) : displayOptions.length > 0 ? (
              displayOptions.map((option) => {
                const isSelected = selectedOption?.value === option.value;
                return (
                <div
                  key={option.value}
                  ref={isSelected ? selectedItemRef : null}
                  className={`p-2 text-gray-950 cursor-pointer sm:text-sm px-3 ${
                    isSelected
                      ? 'bg-lightgreen text-white'
                      : 'hover:bg-gray-100'
                  }`}
                  onClick={() => handleSelect(option)}
                >
                  {option.label}
                  <span className="block text-xs">{option.client_code}</span>
                </div>
                );
              })
            ) : (
              <div className="p-2 text-gray-500 text-center">
                {showTypeToSearch ? 'Continuez à saisir…' : 'Aucun client trouvé'}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UICustomerDropdown;
